/**
 * W13 (DM1) — the `aiMeta.dataMapping.sourcePath` insight grammar, in ONE place.
 *
 * Two shapes, and the second is the first plus a suffix:
 *
 *   thesis  `@insight[<analysisId>].<category>[<insightId>]`
 *   quote   `@insight[<analysisId>].<category>[<insightId>].sources[<originalItemId>]`
 *
 * Ruling W13-R17: **the path is normative**. `evidenceKind` / `actorName` on the
 * dataMapping entry are derived display metadata — a value that disagrees with the
 * path is ignored, never a finding. Ruling W13-R4: a quote citation is legal ONLY
 * next to the thesis citation of the same screen; a lone quote or an item id the
 * pinned package does not know is a WARNING, never a compile blocker.
 *
 * Every class is closed on purpose. The pattern is `$`-anchored, the optional
 * group demands a non-empty key, and the CATEGORY class excludes `.` as well as
 * brackets — so `.sources`, `.sources[]`, `.variants[x]`, a doubled suffix, a
 * dotted category and `@insight[a].fears.sources[item-9]` (a suffix hung on the
 * category, with no thesis) all parse as `null`. A malformed suffix never mints a
 * phantom analysis id or a phantom thesis id (AC-W13-33).
 *
 * MIRROR (ruling W13-R28) — the regex below is BYTE-IDENTICAL to
 * `builder-service/services/cli/insightImpact.ts` (GRAMMAR_1) and
 * `data-pickers/shared/pathParser.ts` (the insight item matcher). The three
 * cannot import one another: builder-service ships as `dist/` with no
 * `@modules/*` resolution (deploy Guard 4), so a value import across that
 * boundary crashes the container at boot. They are therefore kept in sync BY
 * HAND, and each carries the SAME malformed-case table in its own test file
 * (`screenValidator.evidenceCitations.test.ts`, `insightImpact.sourceSubPath.test.ts`,
 * `pathParser.insightSource.test.ts`). Keep all three in step, or none of them
 * is trustworthy. Durable follow-up: publish the pattern as a `shared-types`
 * const string, which crosses the boundary as a type-only package already in
 * builder-service's dependency set.
 */

const INSIGHT_CITATION = /^@insight\[([^\]]+)\]\.([^[\].]+)\[([^\]]+)\](?:\.sources\[([^\]]+)\])?$/;

export interface ParsedInsightCitationPath {
  analysisId: string;
  category: string;
  insightId: string;
  /** Present only for a quote citation. */
  sourceItemId?: string;
  /** The thesis path this citation belongs to — itself for a thesis citation. */
  thesisPath: string;
}

/** Returns null for every path that is not a well-formed `@insight[…]` citation. */
export function parseInsightCitationPath(sourcePath: unknown): ParsedInsightCitationPath | null {
  if (typeof sourcePath !== 'string') return null;
  const match = sourcePath.match(INSIGHT_CITATION);
  if (!match) return null;
  const [, analysisId, category, insightId, sourceItemId] = match;
  return {
    analysisId,
    category,
    insightId,
    ...(sourceItemId ? { sourceItemId } : {}),
    thesisPath: `@insight[${analysisId}].${category}[${insightId}]`,
  };
}

/**
 * A block's citation list: `dataUsage.items` plus the legacy
 * `dataUsage.dataMappingItems`, in that order (the same pair
 * `strategyHealthReport.collectDataUsageVariableTokens` reads). Both arrays are
 * ONE list for the «next to its thesis» rule — a thesis declared in either
 * answers a quote declared in the other.
 */
export function blockDataUsageCitations(block: unknown): unknown[] {
  if (!block || typeof block !== 'object') return [];
  const dataUsage = (block as Record<string, unknown>).dataUsage;
  if (!dataUsage || typeof dataUsage !== 'object') return [];
  const record = dataUsage as Record<string, unknown>;
  return [
    ...(Array.isArray(record.items) ? record.items : []),
    ...(Array.isArray(record.dataMappingItems) ? record.dataMappingItems : []),
  ];
}

/** Is this dataMapping `value` empty (absent, blank, or an empty container)? */
export function isEmptyMappingValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length === 0;
  return false;
}

export interface EvidenceCitationDefect {
  kind: 'source-without-thesis' | 'unknown-source-item' | 'source-empty-value';
  /**
   * The screen id or block key whose citation list holds the defect — one
   * CITATION LIST is the unit the rule is judged over, and a strategy has two of
   * them: a screen's `aiMeta.dataMapping` and a block's `dataUsage` items.
   */
  ownerId: string;
  sourcePath: string;
  thesisPath: string;
  sourceItemId: string;
}

/**
 * The DM1 decision table over ONE citation list.
 *
 * A citation list is a screen's `aiMeta.dataMapping` OR a block's
 * `dataUsage.items` / `dataUsage.dataMappingItems` — the welcome/tasks prompts
 * put the quote entry in `dataUsage`, the block-screen prompts in `dataMapping`,
 * and both must be judged by the same rule. «Next to its thesis» therefore means
 * «in the SAME list»: a block-level quote is answered by a block-level thesis,
 * a screen-level quote by a screen-level thesis.
 *
 * `knownSourceItemIds` is the pinned package's variant keys. When it is absent
 * the unknown-item check is SKIPPED entirely — the validator never guesses which
 * item ids exist (brief item 3, AC-W13-08 case 4).
 *
 * At most one defect per quote citation, in table order: a quote with no thesis
 * in its list is reported as that and nothing else, because the item id of a
 * misplaced citation is not worth a second line.
 */
export function findEvidenceCitationDefects(
  ownerId: string,
  citations: unknown,
  knownSourceItemIds?: Iterable<string>,
): EvidenceCitationDefect[] {
  const entries = Array.isArray(citations) ? citations : [];
  const known = knownSourceItemIds ? new Set(knownSourceItemIds) : undefined;

  const citedTheses = new Set<string>();
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') continue;
    const parsed = parseInsightCitationPath((entry as Record<string, unknown>).sourcePath);
    if (parsed && !parsed.sourceItemId) citedTheses.add(parsed.thesisPath);
  }

  const defects: EvidenceCitationDefect[] = [];
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const parsed = parseInsightCitationPath(record.sourcePath);
    if (!parsed?.sourceItemId) continue;
    const base = {
      ownerId,
      sourcePath: record.sourcePath as string,
      thesisPath: parsed.thesisPath,
      sourceItemId: parsed.sourceItemId,
    };
    if (!citedTheses.has(parsed.thesisPath)) {
      defects.push({ kind: 'source-without-thesis', ...base });
      continue;
    }
    if (known && !known.has(parsed.sourceItemId)) {
      defects.push({ kind: 'unknown-source-item', ...base });
      continue;
    }
    if (isEmptyMappingValue(record.value)) {
      defects.push({ kind: 'source-empty-value', ...base });
    }
  }
  return defects;
}
